/**
 * Reach Wire text (spec §12.1-§12.3): the headline templates, cause phrases and price-chip texts of
 * `src/data/strategic/wire.json` (parsed once by `data.ts`), slot rendering and the seeded variant choice.
 * Text is built at most once per Wire item or view, never per tick, so these functions may allocate.
 */
import { deriveSeed } from '../../galaxy/seed'
import type { StrategicData } from '../data'
import type { WireTemplateKey } from '../schemas'
import type { CauseKindName, PriceCauseTag } from '../types'

/** The `{slot}` values of one rendering. A missing slot renders as the empty string. */
export interface WireFields {
  station?: string
  system?: string
  ware?: string
  faction?: string
  cause?: string
  pct?: string
  /** M5 (C7): a second faction (the previous holder, the other side, the owner). */
  other?: string
  /** M5 (C7): a formatted number (a bounty, tonnes, the gate's stability). */
  amount?: string
  /** M5 (C7): a formatted duration ("2 h"). */
  duration?: string
  /** M5 (C7): the player's name (a bounty). */
  pilot?: string
  /** M5 (C7): a raider clan's name. */
  clan?: string
  /** M6 (P13): the player corporation's name and ticker. */
  corp?: string
  ticker?: string
  /** M6 (P13): a captain's name, a ship's name and class. */
  captain?: string
  ship?: string
  class?: string
  /** M6 (P13): a standing order, a captain skill, a level, a station type, a milestone and a day count. */
  order?: string
  skill?: string
  level?: string
  type?: string
  milestone?: string
  days?: string
  /** M9 (CH-14b): a hull's readable name (wire-m9.json `hullNames`). */
  hull?: string
}

const SLOT = /\{([a-z]+)\}/g

function slotValue(fields: WireFields, slot: string): string {
  switch (slot) {
    case 'station':
      return fields.station ?? ''
    case 'system':
      return fields.system ?? ''
    case 'ware':
      return fields.ware ?? ''
    case 'faction':
      return fields.faction ?? ''
    case 'cause':
      return fields.cause ?? ''
    case 'pct':
      return fields.pct ?? ''
    case 'other':
      return fields.other ?? ''
    case 'amount':
      return fields.amount ?? ''
    case 'duration':
      return fields.duration ?? ''
    case 'pilot':
      return fields.pilot ?? ''
    case 'clan':
      return fields.clan ?? ''
    case 'corp':
      return fields.corp ?? ''
    case 'ticker':
      return fields.ticker ?? ''
    case 'captain':
      return fields.captain ?? ''
    case 'ship':
      return fields.ship ?? ''
    case 'class':
      return fields.class ?? ''
    case 'order':
      return fields.order ?? ''
    case 'skill':
      return fields.skill ?? ''
    case 'level':
      return fields.level ?? ''
    case 'type':
      return fields.type ?? ''
    case 'milestone':
      return fields.milestone ?? ''
    case 'days':
      return fields.days ?? ''
    case 'hull':
      return fields.hull ?? ''
    default:
      return ''
  }
}

/**
 * Fills every `{slot}` of `template`. A ware name inside a sentence is lower-cased ("runs short of food"),
 * and one that opens the sentence keeps its capital ("Food shortage at …").
 */
export function render(template: string, fields: WireFields): string {
  return template.replace(SLOT, (_m: string, slot: string, offset: number) => {
    const v = slotValue(fields, slot)
    if (slot === 'ware' && offset > 0 && v.length > 0) return v.charAt(0).toLowerCase() + v.slice(1)
    return v
  })
}

/** A seeded variant: `deriveSeed(seed, stream, id)` picks one of `variants` (deterministic per item). */
export function pickVariant<T>(variants: readonly T[], seed: number, stream: string, id: number): T {
  if (variants.length === 0) throw new Error(`wire: no variants for ${stream}`)
  return variants[deriveSeed(seed, stream, id) % variants.length]
}

export function headlineTemplates(data: StrategicData, key: WireTemplateKey): readonly string[] {
  return data.wire.templates[key]
}

export function causePhrases(data: StrategicData, kind: CauseKindName): readonly string[] {
  return data.wire.causes[kind]
}

export function priceCauseTemplate(data: StrategicData, tag: PriceCauseTag): string {
  return data.wire.priceCauses[tag]
}

/** Renders the headline of Wire item `id` from its template key, with the item's seeded variant. */
export function renderHeadline(data: StrategicData, seed: number, key: WireTemplateKey, id: number, fields: WireFields): string {
  return render(pickVariant(headlineTemplates(data, key), seed, 'wire', id), fields)
}

/**
 * TR-7: the competing-haulers line a shortage headline ends with, from the count of agents already carrying
 * the ware to the short station (`haulersEnRoute` in `wire.ts`). `{amount}` is the count.
 */
export const HAULERS_EN_ROUTE = {
  none: 'No haulers en route.',
  one: 'One hauler en route.',
  many: '{amount} haulers en route.',
} as const

/** The competing-haulers line for `count` agents en route (0 = none). */
export function haulersLine(count: number): string {
  if (!(count > 0)) return HAULERS_EN_ROUTE.none
  if (count === 1) return HAULERS_EN_ROUTE.one
  return render(HAULERS_EN_ROUTE.many, { amount: String(count) })
}

/** A signed whole percent for the price chips: `+24%`, `-12%`. */
export function formatPct(deltaPct: number): string {
  const r = Math.round(deltaPct)
  return `${r >= 0 ? '+' : '-'}${Math.abs(r)}%`
}
